import { useRef } from 'react';
import { config } from 'config';
import { useAppDispatch, useAppSelector } from 'app/store';
import { Flex } from '@chakra-ui/react';
import {
  getApiKeyWebLoadingState,
  getAuthenticatedSelector,
  getBootstrapLoadingState,
  getLastLoginTime,
} from 'app/store/Auth/selectors';
import {
  useAssistSettingsWs,
  useSyncIntercom,
  useIsMobile,
} from 'app/utils/hooks';
import { ScrollableContent } from 'app/components/ScrollableContent';
import { AudioPlayer } from 'app/components/AudioPlayer';
import {
  AppOverlayPortalContainer,
  AppOverlayPortalContext,
} from 'app/components/AppOverlayPortal';
import { OfflineTracker } from 'app/components/Offline/OfflineTracker';
import { NoCookiesTracker } from 'app/components/NoCookies/NoCookiesTracker';
import { ErrorBoundary } from 'app/components/ErrorBoundary';
import { Outlet } from 'react-router-dom';
import { GlobalDialogs } from 'app/components/Dialogs/GlobalDialogs';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { HeaderBar } from 'app/components/HeaderBar';
import { TitleBar } from 'app/components/TitleBar';
import { SideBar } from 'app/components/SideBar';
import { CoWriterBar } from 'app/components/CoWriterBar';
import { isLoading } from './utils/utils';
import { WebSockets } from './components/WebSockets';
import { GlobalStoragePopUp } from './components/Storage/GlobalStoragePopUp';
import { DragPreview } from './components/Song/SongList/DragPreview';
import { MIN_LAYOUT_WIDTH } from './constants';
import { ProfileBar } from './components/ProfileBar';
import { SongChatBar } from './components/SongChatBar';
import { GlobalSearch } from './components/GlobalSearch/GlobalSearch';
import { useShowGlobalSearch } from './components/GlobalSearch/hooks';

export function App() {
  const dispatch = useAppDispatch();
  const overlayContainer = useRef<HTMLDivElement>(null);
  useAssistSettingsWs(dispatch);
  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const apiKeyLoadingState = useAppSelector(getApiKeyWebLoadingState);
  const bootstrapLoadingState = useAppSelector(getBootstrapLoadingState);
  const lastLogin = useAppSelector(getLastLoginTime);
  const isGlobalSearchOpen = useShowGlobalSearch();
  const isApiKeyLoading = isLoading(apiKeyLoadingState);
  const isMobileBrowser = useIsMobile();

  useSyncIntercom(isAuthenticated);

  return (
    <>
      <AppOverlayPortalContext.Provider value={overlayContainer}>
        <AppOverlayPortalContainer ref={overlayContainer} />
        <Flex
          direction="column"
          overflowY="hidden"
          overflowX="auto"
          position="relative"
          {...(isAuthenticated ? { bg: 'M.20' } : {})}
        >
          <DndProvider
            backend={TouchBackend}
            options={{ enableMouseEvents: true }}
          >
            {config.isElectronBuild ? <TitleBar /> : <HeaderBar isWeb />}
            <Flex
              w="full"
              h="full"
              pr={2}
              mt={12}
              overflow="hidden"
              {...(isMobileBrowser || !isAuthenticated
                ? {}
                : {
                    minW: MIN_LAYOUT_WIDTH,
                  })}
            >
              {isAuthenticated && !isMobileBrowser && <SideBar />}
              <ScrollableContent
                bootstrapLoadingState={bootstrapLoadingState}
                isAuthenticated={isAuthenticated}
                lastLogin={lastLogin}
                isApiKeyLoading={isApiKeyLoading}
                disableScroll={isGlobalSearchOpen}
              >
                <ErrorBoundary>
                  <GlobalDialogs />
                  <GlobalStoragePopUp />
                  <GlobalSearch />
                  <Outlet />
                </ErrorBoundary>
              </ScrollableContent>
              {isAuthenticated && !isMobileBrowser && (
                <>
                  <CoWriterBar />
                  <ProfileBar />
                  <SongChatBar />
                </>
              )}
              <DragPreview />
            </Flex>
          </DndProvider>
        </Flex>
      </AppOverlayPortalContext.Provider>
      {isAuthenticated && <AudioPlayer />}
      <OfflineTracker />
      <NoCookiesTracker />
      <WebSockets />
    </>
  );
}
